import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import {
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
} from '@ngrx/store';
import { ToastModel } from '@syncfusion/ej2-angular-notifications';

import { ToastActions } from '../toast/toast.actions';
import { ErrorDialogActions } from './error-dialog.actions';
import { ErrorDialogEntry } from './error-dialog.model';

export const ERROR_DIALOG_FEATURE_KEY = 'errorDialog';

export interface ErrorDialogState extends EntityState<ErrorDialogEntry> {
  /** Monotonic counter used to assign a unique id to each stacked error. */
  nextId: number;
}

export const errorDialogAdapter: EntityAdapter<ErrorDialogEntry> =
  createEntityAdapter<ErrorDialogEntry>();

export const initialErrorDialogState: ErrorDialogState =
  errorDialogAdapter.getInitialState({ nextId: 1 });

/** Toast title/content are typed `string | HTMLElement`; in practice the toast
 * actions always pass strings, so coerce anything else to an empty string. */
function toText(value: string | HTMLElement | undefined): string {
  return typeof value === 'string' ? value : '';
}

function stackError(
  state: ErrorDialogState,
  toast: ToastModel
): ErrorDialogState {
  const id = state.nextId;
  return errorDialogAdapter.addOne(
    { id, title: toText(toast?.title), content: toText(toast?.content) },
    { ...state, nextId: id + 1 }
  );
}

export const errorDialogReducer = createReducer(
  initialErrorDialogState,
  // Both error toast actions carry a `{ toast }` payload and are stacked here
  // instead of being shown as a short-lived toast.
  on(
    ToastActions.showToastError,
    ToastActions.showToastApiError,
    (state, { toast }) => stackError(state, toast)
  ),
  on(ErrorDialogActions.dismissError, (state, { id }) =>
    errorDialogAdapter.removeOne(id, state)
  ),
  on(ErrorDialogActions.dismissAllErrors, (state) =>
    errorDialogAdapter.removeAll(state)
  )
);

const { selectAll, selectTotal } = errorDialogAdapter.getSelectors();

const selectErrorDialogState = createFeatureSelector<ErrorDialogState>(
  ERROR_DIALOG_FEATURE_KEY
);

const selectAllErrors = createSelector(selectErrorDialogState, selectAll);
const selectErrorCount = createSelector(selectErrorDialogState, selectTotal);
const selectHasErrors = createSelector(
  selectErrorCount,
  (count) => count > 0
);

export const ErrorDialogSelectors = {
  selectAllErrors,
  selectErrorCount,
  selectHasErrors,
};
